import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ProtectedRoute({ children }) {
  const { t } = useTranslation('auth');
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const [authState, setAuthState] = useState({
    isAuthenticated: null,
    accountStatus: null, // 'active', 'pending', 'inactive'
    currentUser: null
  });

  // Function to validate user access from backend
  const validateAccess = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.status === 200) {
        const userData = response.data;
        
        // Determine account status
        let accountStatus = 'active';
        if (!userData.is_active) {
          accountStatus = 'inactive';
        } else if (!userData.can_access_dashboard) {
          accountStatus = 'pending';
        }
        
        setAuthState({
          isAuthenticated: true,
          accountStatus,
          currentUser: userData
        });
        
        return { isAuthenticated: true, accountStatus };
      }
    } catch (error) {
      console.error('Auth validation failed:', error);
      
      // Check if the error is due to inactive account
      // X-Account-Status: the detail text is translated, so don't match on it
      if (error.response?.status === 403 && (error.response?.headers?.['x-account-status'] === 'inactive'
        || error.response?.data?.detail?.includes('deactivated'))) {
        setAuthState({
          isAuthenticated: true,
          accountStatus: 'inactive',
          currentUser: null
        });
        return { isAuthenticated: true, accountStatus: 'inactive' };
      }
      
      setAuthState({
        isAuthenticated: false,
        accountStatus: null,
        currentUser: null
      });
      return { isAuthenticated: false, accountStatus: null };
    }
  }, []);

  // Validate on initial load and on route changes
  useEffect(() => {
    // Skip if we're on the callback page (OAuth flow)
    if (window.location.hash?.includes('session_id=')) {
      return;
    }
    
    validateAccess();
  }, [location.pathname, validateAccess]);

  // Also refresh auth context when validation completes
  useEffect(() => {
    if (authState.isAuthenticated && authState.accountStatus === 'active') {
      // Sync auth context with latest user data
      checkAuth();
    }
  }, [authState.isAuthenticated, authState.accountStatus, checkAuth]);

  // Loading state
  if (authState.isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700 mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">{t('verifying')}</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Account is inactive - redirect to inactive page
  if (authState.accountStatus === 'inactive') {
    return <Navigate to="/inactive" state={{ user: authState.currentUser }} replace />;
  }

  // Authenticated but pending approval or no project access
  if (authState.accountStatus === 'pending') {
    return <Navigate to="/pending" state={{ user: authState.currentUser }} replace />;
  }

  return children;
}

export default ProtectedRoute;
